import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes, type ReactElement } from 'react';

import { cn } from '../lib/cn.js';

/**
 * Variantes do Badge. Usa tokens semânticos (primary/secondary/destructive/
 * accent) — nenhum hex literal.
 *
 * - default    → neutro (contagens, labels genéricos)
 * - secondary  → info sutil
 * - success    → status positivo (ok, ativo, "Conectado")
 * - destructive → erro / alerta crítico
 * - outline    → apenas borda (contadores de lista)
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary/10 text-primary',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        success: 'border-transparent bg-success/15 text-success dark:bg-success/20',
        destructive: 'border-transparent bg-destructive/10 text-destructive',
        outline: 'text-foreground border-border',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, variant, ...props },
  ref,
): ReactElement {
  return <span ref={ref} className={cn(badgeVariants({ variant, className }))} {...props} />;
});
